import { computed, onMounted, onScopeDispose, ref } from "vue";
import envOptions from "../Env";
import eventHub from "@/helpers/utility/Events";
import { useUndo } from "@editor/store/undo/undo.store";
import { useRouter } from "vue-router";

const modelUndoStateOnLastSave = ref<number | undefined | null>(null);
const simulationUndoStateOnLastSave = ref<number | undefined | null>(null);
const evaluationUndoStateOnLastSave = ref<number | undefined | null>(null);

export function didAnythingChange() {
  // Nothing changed if we are at the same undo entry since last save:
  if (useUndo().getStack("model")?.lastActionId.value !== modelUndoStateOnLastSave.value) {
    return true;
  }

  if (useUndo().getStack("simulation")?.lastActionId.value !== simulationUndoStateOnLastSave.value) {
    return true;
  }

  if (useUndo().getStack("evaluation")?.lastActionId.value !== evaluationUndoStateOnLastSave.value) {
    return true;
  }

  return false;
}

export function useUnloadGuard() {
  const router = useRouter();

  const undoStore = useUndo();

  const modelChanged = computed(() => {
    // Nothing changed if we are at the same undo entry since last save:
    const modelActionId = undoStore.getStack("model")?.lastActionId.value;
    if (modelActionId !== modelUndoStateOnLastSave.value) {
      return true;
    }

    const simulationActionId = undoStore.getStack("simulation")?.lastActionId.value;
    if (simulationActionId !== simulationUndoStateOnLastSave.value) {
      return true;
    }

    const evaluationActionId = undoStore.getStack("evaluation")?.lastActionId.value;
    if (evaluationActionId !== evaluationUndoStateOnLastSave.value) {
      return true;
    }

    return false;
  });

  function updateUndoStateOnLastSave() {
    modelUndoStateOnLastSave.value = useUndo().getStack("model")?.lastActionId.value;
    simulationUndoStateOnLastSave.value = useUndo().getStack("simulation")?.lastActionId.value;
    evaluationUndoStateOnLastSave.value = useUndo().getStack("evaluation")?.lastActionId.value;
  }

  function onUnload(event: BeforeUnloadEvent) {
    // always prevent unloading if we're in simulation/evaluation
    const routeAreaName = router.currentRoute.value.meta?.areaName;
    if (routeAreaName !== "simulation" && routeAreaName !== "evaluation") {
      if (!didAnythingChange()) return;
    }

    // browser will show a standard "do you really want to leave" dialog.
    event.returnValue = "";
    event.preventDefault();
  }

  function addUnloadListener() {
    // ask for confirmation before closing when online
    if (!envOptions.isElectron) {
      window.addEventListener("beforeunload", onUnload);
    }
  }

  function removeUnloadListener() {
    // ask for confirmation before closing when online
    if (!envOptions.isElectron) {
      window.removeEventListener("beforeunload", onUnload);
    }
  }

  function addUnloadGuardListener() {
    addUnloadListener();
    onScopeDispose(() => {
      removeUnloadListener();
    });
  }

  onMounted(() => {
    eventHub.on("model-saved", updateUndoStateOnLastSave);
  });
  onScopeDispose(() => {
    eventHub.off("model-saved", updateUndoStateOnLastSave);
  });

  return {
    modelChanged,
    didAnythingChange,
    addUnloadGuardListener
  };
}
